import { useEffect, useMemo, useState, type FormEvent } from 'react'
import { useQuery } from '@tanstack/react-query'
import { FolderField } from '../servers/FolderField'
import { onStreamOpen } from '../servers/commandStream'
import { ApiError } from '../transport/http'
import { agentRestore, agentRestorePrepare, agentRestoreStatus, type RestoreJob } from '../transport/api'
import { getAccountDevices, getBackupKey, getVault, type BackupRow } from '../transport/relay'
import { deviceDisplayName } from '../transport/deviceName'
import { formatBytes } from '../ui/bytes'
import type { StoredVault } from '../transport/relay'
import { FaceIdButton, useFaceId } from './FaceIdButton'
import { FaceIdFailed, unlockWithFaceId } from './passkey'
import { WrongSecret, openVault, sealForMachine, unlockWithPassword, unwrapBackupKey } from './vaultCrypto'
import { isOver, noteRestoreJob, useRestoreJob } from './restoreJobs'

/**
 * BRING A BACKUP BACK — owner, 2026-09-29: 從 More 裡面那個其中一個備份, 使用者點擊之後,
 * 可以讓他選擇要用它複製到哪一個地方去 … 選擇他想要複製過去的機器以及 … folder, 還有,
 * 它可以重新修改複製過去 Agent 的名稱.
 *
 * The machine, the folder, the name — then the vault password, because only this device
 * can open the backup's key: it opens it with the vault and seals it to the machine's
 * one-time key, so the relay and the cloud carry a sealed box and nothing else. The
 * machine does the rest (edge agentRestore.ts) and announces each step.
 */
const VAULT_USERNAME = 'Avengents backup vault'

function restoreLine(j: RestoreJob, machine: string): string {
  switch (j.phase) {
    case 'queued':
      return `Waiting for another restore on ${machine} to finish…`
    case 'fetching':
      return 'Asking the cloud for the backup…'
    case 'downloading':
      return `Downloading and decrypting on ${machine}… ${j.done} of ${j.total} part${j.total === 1 ? '' : 's'}`
    case 'unpacking':
      return 'Putting its files in place…'
    case 'starting':
      return `Starting ${j.session}…`
    case 'done':
      return `Restored as ${j.session} on ${machine}.`
    case 'failed':
      return j.message || 'The restore did not finish.'
  }
}

function whyRestoreFailed(e: unknown, ctx: { machine: string; name: string }): string {
  if (e instanceof WrongSecret) return 'That password does not open your vault.'
  if (e instanceof ApiError) {
    const b = (e.body ?? {}) as { error?: string; message?: string }
    if (b.error === 'already_exists') return `An agent named ${ctx.name} already exists on ${ctx.machine} — choose another name.`
    if (e.status === 404 && (!b.error || b.error === 'not_found')) return `${ctx.machine} needs an Avengents update before it can restore backups.`
    return b.message || b.error || e.message
  }
  return e instanceof Error ? e.message : String(e)
}

export function RestoreForm({
  backup,
  onClose,
  onOpenAgent,
}: {
  backup: BackupRow
  onClose: () => void
  onOpenAgent?: (a: { session: string; hash: string; device: string }) => void
}) {
  const devices = useQuery({ queryKey: ['menu-machines'], queryFn: getAccountDevices, staleTime: 30_000 })
  const vault = useQuery({ queryKey: ['backup-vault'], queryFn: getVault, staleTime: 30_000 })
  const machines = useMemo(() => devices.data?.devices ?? [], [devices.data])
  const [device, setDevice] = useState(backup.device_id)
  const [cwd, setCwd] = useState('')
  const [name, setName] = useState(backup.session)
  const [pw, setPw] = useState('')
  /*
   * THE NAME IS LOCKED WHILE THE PASSWORD IS BEING FILLED — owner, 2026-09-30: Apple
   * Passwords filled the saved USERNAME into the Agent name field along with the password.
   * A password manager treats the text field before a password as its username field; a
   * read-only field is one it skips. So focusing the password locks the name, and the
   * person's own tap (or focus) on the name unlocks it again for editing.
   *
   * AND THE FOLDER WITH IT — owner, 2026-10-13: with the name skipped, the username went
   * one field further up (除非使用者點擊 Folder 那一欄, 不然 … 不可以被任何東西輸入). Each
   * field is unlocked by a tap on itself, never by a tap on the other.
   */
  const [nameLocked, setNameLocked] = useState(false)
  const [folderLocked, setFolderLocked] = useState(false)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [jobId, setJobId] = useState<string | null>(null)
  const job = useRestoreJob(jobId)

  // Where it was backed up is the first choice, when that machine is still paired.
  useEffect(() => {
    if (!machines.length) return
    if (!machines.some((m) => m.device_id === device)) setDevice(machines[0]!.device_id)
  }, [machines, device])

  const row = machines.find((m) => m.device_id === device)
  const machine = row ? deviceDisplayName(row) : backup.machine || 'the machine'
  const trimmed = name.trim()
  const nameBad = /[.:\s]/.test(trimmed)
  const readyButPw = !!device && !!cwd.trim() && !!trimmed && !nameBad && !!vault.data?.vault
  const ready = readyButPw && !!pw
  const faceId = useFaceId(vault.data?.vault)

  /* The floor under the events — asked again when the stream reopens or the app comes
     back into view, never on a clock (the owner's standing rule). */
  const running = !!job && !isOver(job)
  useEffect(() => {
    if (!running || !jobId || !job) return
    const reask = () =>
      void agentRestoreStatus(job.device, jobId).then(
        (r) => r.job && noteRestoreJob(job.device, r.job),
        () => undefined,
      )
    const offOpen = onStreamOpen(reask)
    const onVisible = () => {
      if (document.visibilityState === 'visible') reask()
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      offOpen()
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [running, jobId, job])

  const submit = (e: FormEvent) => {
    e.preventDefault()
    if (ready) void run((v) => unlockWithPassword(v, pw))
  }

  /** The restore, from whichever door opened the vault key: the password or Face ID. */
  const run = async (open: (v: StoredVault) => Promise<Uint8Array<ArrayBuffer>>) => {
    if (!readyButPw || busy || !vault.data?.vault) return
    setBusy(true)
    setErr(null)
    const target = device
    let before: Set<string> | null = null
    try {
      const v = vault.data.vault
      const vk = await open(v)
      const priv = await openVault(v, vk)
      vk.fill(0)
      const key = await getBackupKey(backup.backup_id)
      const dek = await unwrapBackupKey(key.wrapped_dek, priv, v.vpk, backup.backup_id)
      const prep = await agentRestorePrepare(target)
      const sealed = await sealForMachine(dek, prep.epk, prep.restore_id)
      dek.fill(0)
      before = await agentRestoreStatus(target).then(
        (s) => new Set((s.jobs ?? []).map((j) => j.job_id)),
        () => null,
      )
      const r = await agentRestore(target, {
        restore_id: prep.restore_id,
        backup_id: backup.backup_id,
        sealed_dek: sealed,
        session: trimmed,
        cwd: cwd.trim(),
        from: backup.session,
      })
      noteRestoreJob(target, r.job)
      setJobId(r.job.job_id)
    } catch (x) {
      // A reply lost on its way back is not a restore that did not happen (the backup
      // card's lesson): ask the machine for a restore it did not have before.
      if (before && x instanceof Error && /did not answer/.test(x.message)) {
        const began = await agentRestoreStatus(target).then(
          (s) => (s.jobs ?? []).find((j) => j.backup_id === backup.backup_id && j.session === trimmed && !before!.has(j.job_id)) ?? null,
          () => null,
        )
        if (began) {
          noteRestoreJob(target, began)
          setJobId(began.job_id)
          setBusy(false)
          return
        }
      }
      setErr(x instanceof FaceIdFailed ? x.message : whyRestoreFailed(x, { machine, name: trimmed }))
    } finally {
      setBusy(false)
    }
  }

  if (job) {
    const failed = job.phase === 'failed'
    const pct =
      job.phase === 'done' ? 100 : job.phase === 'downloading' && job.total ? Math.round((job.done / job.total) * 80) : job.phase === 'unpacking' ? 85 : job.phase === 'starting' ? 95 : 5
    return (
      <div className="sm-fork bk-form bk-restore" data-testid="restore-progress">
        <p className="bk-title">{failed ? 'Restore did not finish' : job.phase === 'done' ? 'Restored' : 'Restoring…'}</p>
        {!failed && (
          <div className="bk-bar" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={pct}>
            <span style={{ width: `${pct}%` }} />
          </div>
        )}
        <p className={`sm-hint${failed ? ' bk-bad' : ''}`} role={failed ? 'alert' : undefined}>
          {restoreLine(job, machine)}
        </p>
        {job.phase === 'done' && (
          <p className="sm-hint">
            It continues the conversation from this backup, in {job.cwd}.
            {job.kept > 0 && ' The folder’s own memory notes were kept.'}
            {job.notes_skipped && ' Its queued notes were not restored.'}
          </p>
        )}
        <div className="sm-actions">
          <button type="button" className="sheet-btn" onClick={onClose}>
            {isOver(job) ? 'Done' : 'Hide'}
          </button>
          {failed && (
            <button type="button" className="sheet-btn primary" onClick={() => setJobId(null)}>
              Try again
            </button>
          )}
          {job.phase === 'done' && onOpenAgent && (
            <button
              type="button"
              className="sheet-btn primary"
              onClick={() => onOpenAgent({ session: job.session, hash: job.hash, device: job.device })}
            >
              Open it
            </button>
          )}
        </div>
      </div>
    )
  }

  return (
    <form className="sm-fork bk-form bk-restore" onSubmit={(e) => void submit(e)}>
      <p className="bk-title">Restore this backup</p>
      <p className="sm-hint bk-lead">
        From {new Date(backup.created_at * 1000).toLocaleString()} · {formatBytes(backup.packed_size)}. It comes back as a new
        agent that continues this conversation; the agent it came from is not touched.
      </p>
      <div className="sm-field">
        <span className="sm-label">Machine</span>
        <select className="sm-input" aria-label="Machine" value={device} disabled={busy} onChange={(e) => {
          setDevice(e.target.value)
          // The folder belongs to the machine it is on; FolderField asks the new one.
          setCwd('')
        }}>
          {machines.map((m) => (
            <option key={m.device_id} value={m.device_id} disabled={m.live === false}>
              {deviceDisplayName(m)}
              {m.device_id === backup.device_id ? ' (where it was backed up)' : ''}
              {m.live === false ? ' — offline' : ''}
            </option>
          ))}
        </select>
      </div>
      <div className="sm-field">
        <span className="sm-label">Folder</span>
        {device && <FolderField deviceId={device} serverName={machine} value={cwd} onChange={(p) => setCwd(p)} locked={folderLocked} onUnlock={() => setFolderLocked(false)} />}
      </div>
      <label className="sm-field">
        <span className="sm-label">Agent name</span>
        <input
          className={`sm-input${nameLocked ? ' bk-name-locked' : ''}`}
          name="agent-name"
          autoComplete="off"
          value={name}
          spellCheck={false}
          autoCapitalize="off"
          autoCorrect="off"
          disabled={busy}
          readOnly={nameLocked}
          aria-readonly={nameLocked}
          title={nameLocked ? 'Tap to edit the name' : undefined}
          onPointerDown={() => setNameLocked(false)}
          onFocus={() => setNameLocked(false)}
          onChange={(e) => {
            if (!nameLocked) setName(e.target.value)
          }}
        />
      </label>
      {nameLocked && <p className="sm-hint bk-name-hint">Tap the name to change it.</p>}
      {nameBad && <p className="sm-hint bk-bad">A name has no spaces, dots or colons.</p>}
      <input type="text" name="username" autoComplete="username" value={VAULT_USERNAME} readOnly tabIndex={-1} aria-hidden="true" className="bk-username" />
      <label className="sm-field">
        <span className="sm-label">Vault password</span>
        <input
          className="sm-input"
          type="password"
          name="password"
          autoComplete="current-password"
          value={pw}
          disabled={busy}
          onFocus={() => {
            setNameLocked(true)
            setFolderLocked(true)
          }}
          onChange={(e) => setPw(e.target.value)}
        />
      </label>
      <p className="sm-hint">Your password opens the backup on this device; the machine receives its key sealed, and we never see it.</p>
      {!vault.isLoading && !vault.data?.vault && <p className="sm-hint bk-bad">This account has no backup vault any more — its backups cannot be opened.</p>}
      {err && (
        <p className="err-line" role="alert">
          {err}
        </p>
      )}
      <div className="sm-actions">
        <button type="button" className="sheet-btn" disabled={busy} onClick={onClose}>
          Cancel
        </button>
        {faceId && (
          <FaceIdButton
            label="Face ID"
            disabled={!readyButPw || busy}
            onClick={() => void run((v) => unlockWithFaceId(v))}
          />
        )}
        <button type="submit" className="sheet-btn primary" disabled={!ready || busy}>
          {busy ? 'Opening…' : 'Restore'}
        </button>
      </div>
    </form>
  )
}
